import { ShieldCheck } from 'lucide-react';

import type { StorefrontReviewPolicy } from '@/lib/api/storefrontAdminHooks';
import { cn } from '@/lib/cn';

import type { Tone } from './storefrontFormat';
import { Pill } from './ui';

/*
 * Review policy (contract v7): which owner publishes of a live or paused
 * store wait for a human. Chosen on the store's first approval and changed
 * from the store's page. Whatever the policy, the safety floor below holds.
 */

export interface ReviewPolicyOption {
  key: StorefrontReviewPolicy;
  label: string;
  /** One line: what waits and what goes live at once. */
  explanation: string;
  tone: Tone;
  recommended?: boolean;
}

export const REVIEW_POLICIES: ReviewPolicyOption[] = [
  {
    key: 'every_change',
    label: 'Every change',
    explanation:
      'Every edit waits for review: wording, images, colours, layout, products, prices and promo codes.',
    tone: 'info',
  },
  {
    key: 'copy_and_images',
    label: 'Wording & images',
    explanation:
      'New wording and new images wait. Colours, template, layout, products and prices go live at once.',
    tone: 'neutral',
    recommended: true,
  },
  {
    key: 'flagged_only',
    label: 'Flagged only',
    explanation:
      'Only wording the claims screen flags, and new images, wait. Clean wording goes live at once.',
    tone: 'warning',
  },
  {
    key: 'never',
    label: 'Never',
    explanation: 'Nothing waits. Every change the owner publishes goes live at once.',
    tone: 'danger',
  },
];

export const DEFAULT_REVIEW_POLICY: StorefrontReviewPolicy = 'copy_and_images';

export function reviewPolicyOption(policy: StorefrontReviewPolicy): ReviewPolicyOption {
  return REVIEW_POLICIES.find((p) => p.key === policy) ?? REVIEW_POLICIES[1];
}

export function isReviewPolicy(value: string | null | undefined): value is StorefrontReviewPolicy {
  return REVIEW_POLICIES.some((p) => p.key === value);
}

/** "Review: Wording & images" (the list row and the store header). */
export function ReviewPolicyPill({
  policy,
  className,
}: {
  policy: StorefrontReviewPolicy;
  className?: string;
}) {
  const option = reviewPolicyOption(policy);
  return (
    <Pill tone={option.tone} className={cn('normal-case', className)}>
      <span className="sr-only">Review policy: </span>
      <span aria-hidden>Review:</span> {option.label}
    </Pill>
  );
}

/** What holds whatever the policy. */
export function ReviewPolicySafetyNote() {
  return (
    <p className="flex items-start gap-8 border border-line-subtle bg-raised px-10 py-8 type-body-sm text-ink-secondary leading-130">
      <ShieldCheck size={14} className="mt-2 shrink-0 text-ink-muted" aria-hidden />
      <span>
        Always on, whatever the policy: wording that breaks a claims rule is refused when the owner
        saves, prices stay at or above floor, every published change is recorded in History with an
        ops Slack notice and listed under Published without review, and you can always revert to the
        last approved version.
      </span>
    </p>
  );
}

/** The four policies as radio cards (a fieldset with its own legend). */
export function ReviewPolicyChooser({
  value,
  onChange,
  current,
  legend = 'Review policy',
  hint,
  name = 'review-policy',
  autoFocus = false,
}: {
  value: StorefrontReviewPolicy;
  onChange: (next: StorefrontReviewPolicy) => void;
  /** The store's policy now, marked "Current" (a change, not a first choice). */
  current?: StorefrontReviewPolicy;
  legend?: string;
  /** A sentence under the legend. */
  hint?: string;
  name?: string;
  /** Focus the chosen card when the dialog opens. */
  autoFocus?: boolean;
}) {
  return (
    <fieldset className="flex flex-col gap-8 min-w-0">
      <legend className="type-label-xs text-ink-muted mb-6">{legend}</legend>
      {hint && <p className="-mt-4 type-body-sm text-ink-secondary leading-130">{hint}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        {REVIEW_POLICIES.map((option) => {
          const checked = option.key === value;
          return (
            <label
              key={option.key}
              className={cn(
                'relative flex items-start gap-10 px-12 py-10 border cursor-pointer transition-colors',
                'focus-within:shadow-focus',
                checked ? 'border-ink bg-raised' : 'border-line bg-page hover:bg-sunken',
              )}
            >
              <input
                type="radio"
                name={name}
                value={option.key}
                checked={checked}
                onChange={() => onChange(option.key)}
                autoFocus={autoFocus && checked}
                className="mt-3 shrink-0 accent-ink"
              />
              <span className="flex flex-col gap-4 min-w-0">
                <span className="flex flex-wrap items-center gap-6">
                  <span className="type-label-sm text-ink">{option.label}</span>
                  {option.recommended && (
                    <span className="type-label-xs text-success">Recommended</span>
                  )}
                  {current === option.key && (
                    <span className="type-label-xs text-ink-muted">Current</span>
                  )}
                </span>
                <span className="type-body-sm text-ink-secondary leading-130">
                  {option.explanation}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
